 import { ReactNode } from 'react';
 import { Navigate, useLocation } from 'react-router-dom';
 import { useAuth } from '@/contexts/AuthContext';
 import { useDealershipScope, getDealerAccessBlockReason } from '@/hooks/useDealershipScope';
 import { Loader2 } from 'lucide-react';
 
 // Import dealer-specific status pages
 import { NotAuthorizedDealerPage } from '@/pages/dealer/NotAuthorizedDealerPage';
 import { DealerPendingPage } from '@/pages/dealer/DealerPendingPage';
 import { DealerSuspendedPage } from '@/pages/dealer/DealerSuspendedPage';
 import { DealerMemberInactivePage } from '@/pages/dealer/DealerMemberInactivePage';
 
 /**
  * Loading spinner for dealer auth checks
  */
 function DealerAuthLoader() {
   return (
     <div className="min-h-screen flex items-center justify-center bg-background">
       <Loader2 className="h-8 w-8 animate-spin text-primary" />
     </div>
   );
 }
 
 interface DealerRouteGuardProps {
   children: ReactNode;
 }
 
 /**
  * RequireDealerMember - Guards routes requiring any dealer membership
  * 
  * Guard ordering (non-negotiable):
  * 1. Not authenticated → redirect /dealer/login
  * 2. Authenticated but not email verified → redirect /auth/verify
  * 3. Authenticated + verified but not a dealer user → NotAuthorizedDealerPage
  * 4. Dealer user but orgStatus = 'suspended' → DealerSuspendedPage
  * 5. Dealer user but orgStatus = 'pending' → DealerPendingPage
  * 6. Dealer user + org approved but memberStatus = 'inactive' → DealerMemberInactivePage
  * 7. All checks pass → render children
  */
 export function RequireDealerMember({ children }: DealerRouteGuardProps) {
   const { user, isLoading: authLoading, isEmailVerified } = useAuth();
   const scope = useDealershipScope();
   const location = useLocation();
 
   // Step 0: Wait for auth and scope to load
   if (authLoading || scope.isLoading) {
     return <DealerAuthLoader />;
   }
 
   // Step 1: Not authenticated → redirect to dealer login
   if (!user) {
     const next = encodeURIComponent(location.pathname + location.search);
     return <Navigate to={`/dealer/login?next=${next}`} replace />;
   }
 
   // Step 2: Authenticated but email not verified → redirect to verify
   if (!isEmailVerified) {
     return <Navigate to="/auth/verify" replace />;
   }
 
   // Step 3: Verified but not a dealer user → NotAuthorizedDealerPage
   if (!scope.isDealer) {
     return <NotAuthorizedDealerPage />;
   }
 
   // Step 4: Dealer user but org suspended → DealerSuspendedPage
   if (scope.orgStatus === 'suspended') {
     return <DealerSuspendedPage />;
   }
 
   // Step 5: Dealer user but org pending → DealerPendingPage
   if (scope.orgStatus === 'pending') {
     return <DealerPendingPage />;
   }
 
   // Step 6: Dealer user + org approved but member inactive → DealerMemberInactivePage
   if (scope.memberStatus === 'inactive') {
     return <DealerMemberInactivePage />;
   }
 
   // Step 7: All checks pass - render protected content
   return <>{children}</>;
 }
 
 /**
  * RequireDealerAdmin - Guards routes requiring dealer admin role
  * 
  * Same guard ordering as RequireDealerMember, plus:
  * - If user is sales role → NotAuthorizedDealerPage (not admin)
  * 
  * Use for: /dealer/settings, /dealer/team, /dealer/invites
  */
 export function RequireDealerAdmin({ children }: DealerRouteGuardProps) {
   const { user, isLoading: authLoading, isEmailVerified, isAdmin } = useAuth();
   const scope = useDealershipScope();
   const location = useLocation();
 
   // Step 0: Wait for auth and scope to load
   if (authLoading || scope.isLoading) {
     return <DealerAuthLoader />;
   }
 
   // Step 1: Not authenticated → redirect to dealer login
   if (!user) {
     const next = encodeURIComponent(location.pathname + location.search);
     return <Navigate to={`/dealer/login?next=${next}`} replace />;
   }
 
   // Step 2: Authenticated but email not verified → redirect to verify
   if (!isEmailVerified) {
     return <Navigate to="/auth/verify" replace />;
   }
 
   // Allow super admins to access dealer admin routes for support
   if (isAdmin) {
     return <>{children}</>;
   }
 
   // Step 3: Verified but not a dealer user → NotAuthorizedDealerPage
   if (!scope.isDealer) {
     return <NotAuthorizedDealerPage />;
   }
 
   // Step 4: Dealer user but org suspended → DealerSuspendedPage
   if (scope.orgStatus === 'suspended') {
     return <DealerSuspendedPage />;
   }
 
   // Step 5: Dealer user but org pending → DealerPendingPage
   if (scope.orgStatus === 'pending') {
     return <DealerPendingPage />;
   }
 
   // Step 6: Dealer user + org approved but member inactive → DealerMemberInactivePage
   if (scope.memberStatus === 'inactive') {
     return <DealerMemberInactivePage />;
   }
 
   // Step 7: Must be dealer admin (not sales)
   if (!scope.isDealerAdmin) {
     return <NotAuthorizedDealerPage adminRequired />;
   }
 
   // All checks pass - render protected content
   return <>{children}</>;
 }